import React, { useState, useEffect } from 'react'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiUsers, FiUserPlus, FiMail, FiMoreVertical, FiTrash2, FiEdit3, FiEye, FiSettings } = FiIcons

const CollaborationPanel = ({ projectId, onClose }) => {
  const [collaborators, setCollaborators] = useState([])
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('viewer')
  const [loading, setLoading] = useState(false)
  const [activeMenu, setActiveMenu] = useState(null)

  useEffect(() => {
    // Load existing collaborators
    setCollaborators([
      {
        id: 1,
        name: 'Raymond Gey',
        email: 'raymongey@gmail.com',
        role: 'owner',
        avatar: null,
        lastActive: '2 minutes ago',
        status: 'online'
      },
      {
        id: 2,
        name: 'Sarah Johnson',
        email: 'sarah.j@example.com',
        role: 'editor',
        avatar: 'https://images.unsplash.com/photo-1494790108755-2616c9c0c8fb?w=40&h=40&fit=crop&crop=face',
        lastActive: '1 hour ago',
        status: 'offline'
      },
      {
        id: 3,
        name: 'Mike Chen',
        email: 'mike.chen@example.com',
        role: 'viewer',
        avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face',
        lastActive: '3 days ago',
        status: 'offline'
      }
    ])
  }, [projectId])

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return

    setLoading(true)
    // Simulate API call
    setTimeout(() => {
      const newCollaborator = {
        id: Date.now(),
        name: inviteEmail.split('@')[0],
        email: inviteEmail,
        role: inviteRole,
        avatar: null,
        lastActive: 'Just invited',
        status: 'pending'
      }
      setCollaborators([...collaborators, newCollaborator])
      setInviteEmail('')
      setLoading(false)
      alert('Invitation sent successfully!')
    }, 1000)
  }

  const handleRemoveCollaborator = (collaboratorId) => {
    if (confirm('Are you sure you want to remove this collaborator?')) {
      setCollaborators(collaborators.filter(c => c.id !== collaboratorId))
    }
    setActiveMenu(null)
  }

  const handleRoleChange = (collaboratorId, newRole) => {
    setCollaborators(collaborators.map(c => 
      c.id === collaboratorId ? { ...c, role: newRole } : c
    ))
    setActiveMenu(null)
  }

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner': return FiSettings
      case 'editor': return FiEdit3
      case 'viewer': return FiEye
      default: return FiUsers
    }
  }

  const getRoleColor = (role) => {
    switch (role) {
      case 'owner': return 'text-purple-600 bg-purple-100'
      case 'editor': return 'text-blue-600 bg-blue-100'
      case 'viewer': return 'text-gray-600 bg-gray-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  return (
    <div className="bg-white border-l border-gray-200 w-80 h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
            <SafeIcon icon={FiUsers} className="w-5 h-5" />
            <span>Collaboration</span>
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiUsers} className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Invite Form */}
        <div className="space-y-3">
          <div>
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="Enter email address"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
            />
          </div>
          <div className="flex space-x-2">
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm appearance-none bg-white"
            >
              <option value="viewer">Viewer</option>
              <option value="editor">Editor</option>
            </select>
            <button
              onClick={handleInvite}
              disabled={loading || !inviteEmail.trim()}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-1 text-sm"
            >
              <SafeIcon icon={FiUserPlus} className="w-4 h-4" />
              <span>{loading ? 'Inviting...' : 'Invite'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Collaborators List */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-3">
          {collaborators.map((collaborator) => (
            <div
              key={collaborator.id}
              className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {/* Avatar */}
              <div className="relative">
                {collaborator.avatar ? (
                  <img
                    src={collaborator.avatar}
                    alt={collaborator.name}
                    className="w-8 h-8 rounded-full"
                  />
                ) : (
                  <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                    <span className="text-xs font-medium text-gray-600">
                      {collaborator.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                {collaborator.status === 'online' && (
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 border-2 border-white rounded-full"></div>
                )}
                {collaborator.status === 'pending' && (
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-yellow-400 border-2 border-white rounded-full"></div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {collaborator.name}
                  </p>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center space-x-1 ${getRoleColor(collaborator.role)}`}>
                    <SafeIcon icon={getRoleIcon(collaborator.role)} className="w-3 h-3" />
                    <span>{collaborator.role}</span>
                  </span>
                </div>
                <p className="text-xs text-gray-500 truncate">{collaborator.email}</p>
                <p className="text-xs text-gray-400">{collaborator.lastActive}</p>
              </div>

              {/* Actions */}
              {collaborator.role !== 'owner' && (
                <div className="relative">
                  <button
                    onClick={() => setActiveMenu(activeMenu === collaborator.id ? null : collaborator.id)}
                    className="p-1 hover:bg-gray-200 rounded transition-colors"
                  >
                    <SafeIcon icon={FiMoreVertical} className="w-4 h-4 text-gray-400" />
                  </button>

                  {activeMenu === collaborator.id && (
                    <>
                      <div 
                        className="fixed inset-0 z-40" 
                        onClick={() => setActiveMenu(null)}
                      ></div>
                      <div className="absolute right-0 top-8 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 min-w-[120px]">
                        <button
                          onClick={() => handleRoleChange(collaborator.id, collaborator.role === 'editor' ? 'viewer' : 'editor')}
                          className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full text-left"
                        >
                          <SafeIcon icon={collaborator.role === 'editor' ? FiEye : FiEdit3} className="w-4 h-4" />
                          <span>Make {collaborator.role === 'editor' ? 'Viewer' : 'Editor'}</span>
                        </button>
                        <button
                          onClick={() => handleRemoveCollaborator(collaborator.id)}
                          className="flex items-center space-x-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 w-full text-left"
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                          <span>Remove</span>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200">
        <div className="text-xs text-gray-500 text-center">
          {collaborators.length} collaborator{collaborators.length !== 1 ? 's' : ''}
        </div>
      </div>
    </div>
  )
}

export default CollaborationPanel